import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const Home = () => {
  const shortStory = "With Over 30 years of Experience in Auto Services Work";

  return (
    <>
      <Helmet>
        <title>Advanced Automotive Smog | Smog Check & Auto Repair in Temecula</title>
        <meta
          name="description"
          content="Looking for a fast & reliable smog check in Temecula? Advanced Automotive Smog offers expert emissions testing, diagnostics, factory maintenance, and more. Visit us today!"
        />
        <meta
          name="keywords"
          content="smog check near me, smog check Temecula, emissions test Temecula, auto repair shop, vehicle diagnostics, brake repair, AC repair, best car maintenance, high-end car smog testing, smog test for luxury vehicles"
        />
        <meta
          property="og:title"
          content="Advanced Automotive Smog | Smog Check & Auto Repair in Temecula"
        />
        <meta
          property="og:description"
          content="Need a quick & reliable smog check? Advanced Automotive Smog provides expert emissions testing and auto repair services in Temecula. Visit us today!"
        />
        <meta
          property="og:image"
          content="https://www.advancedautomotivesmog.com/assets/home.jpeg"
        />
        <meta property="og:url" content="https://www.advancedautomotivesmog.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Advanced Automotive Smog | Smog Check & Auto Repair in Temecula"
        />
        <meta
          name="twitter:description"
          content="Trusted smog check & auto repair services in Temecula. Fast emissions testing, diagnostics, and maintenance. Visit Advanced Automotive Smog today!"
        />
        <meta
          name="twitter:image"
          content="https://www.advancedautomotivesmog.com/assets/home.jpeg"
        />
        <link rel="canonical" href="https://www.advancedautomotivesmog.com/" />
      </Helmet>

      {/* Hero Section */}
      <div className="pt-[120px] pb-12 p-6 bg-cover bg-center">
        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-8">

          {/* Left Content */}
          <div className="flex justify-center items-start bg-transparent p-6">
            <div className="text-center text-white w-full max-w-3xl z-10">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-10 leading-tight">
                Welcome to Advanced Automotive Smog
              </h1>

              <p className="text-xl md:text-2xl lg:text-3xl font-semibold mb-0">
                {shortStory}
              </p>

              <p className="text-lg md:text-xl lg:text-2xl font-thin mb-6">
               We service all vehicles—but specialize in European and electric models. Land Rover, Mercedes, BMW, and Jaguar owners trust us to get it done right—every time.
              </p>

              {/* 📞 Call Us Button */}
              <div className="text-center mt-12 w-full">
                <a
                  href="tel:+19512961242"
                  aria-label="Call Advanced Automotive Smog"
                  className="inline-flex items-center justify-center gap-3 px-12 py-5 bg-cyan-500 sm:text-xl md:text-2xl text-white font-bold rounded-full hover:bg-cyan-600 transition duration-300"
                >
                  <span className="text-white text-2xl">📞</span> Call Us
                </a>
              </div>
            </div>
          </div>

          {/* Right Service Grid */}
          <div className="flex justify-center items-start bg-transparent p-6 backdrop-blur-lg bg-opacity-40 rounded-lg shadow-xl">
            <div className="w-full max-w-5xl">
              <h2 className="text-3xl font-semibold mb-6 text-center text-white">Our Services</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {/* Repeatable Service Cards */}
                {[
                  {
                    title: "Smog & Emissions Services",
                    icon: "fas fa-gas-pump",
                    desc: "Smog checks, emissions diagnostics, and repairs.",
                    link: "/services/smog-emissions-services",
                  },
                  {
                    title: "All Factory Maintenance",
                    icon: "fas fa-wrench",
                    desc: "Comprehensive factory maintenance services for optimal vehicle performance.",
                    link: "/services/all-factory",
                  },
                  {
                    title: "Electrical & Diagnostics",
                    icon: "fas fa-plug",
                    desc: "Check engine light, alternator repair, diagnostics for modern vehicles.",
                    link: "/services/electrical-diagnostics",
                  },
                  {
                    title: "Air Conditioning",
                    icon: "fas fa-cogs",
                    desc: "AC repairs, refrigerant recharges, and system maintenance.",
                    link: "/services/air-conditioning",
                  },
                  {
                    title: "Brakes & Suspension",
                    icon: "fas fa-car-crash",
                    desc: "Brake repairs, shock replacements, and suspension services.",
                    link: "/services/brake-suspension",
                  },
                  {
                    title: "Tire Services / Alignment",
                    icon: "fas fa-tire-flat",
                    desc: "Tire services, alignments, rotations, balancing, and replacements",
                    link: "/services/tire-alignment",
                  },
                ].map((service, idx) => (
                  <Link
                    to={service.link}
                    key={idx}
                    className="relative group bg-gray-900 p-6 rounded-lg shadow-lg overflow-hidden cursor-pointer flex flex-col justify-between transition-all duration-300 ease-in-out transform hover:scale-105 hover:bg-gray-800"
                  >
                    <div className="z-10 text-center">
                      <i className={`text-5xl text-white ${service.icon} mb-4`}></i>
                      <h3 className="text-xl font-semibold mb-2 text-cyan-500 group-hover:text-cyan-400">
                        {service.title}
                      </h3>
                      <p className="text-lg text-white mb-4">{service.desc}</p>
                      <p className="text-gray-400 group-hover:text-white font-semibold transition-colors duration-300">
                        Learn More
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              {/* View All Button */}
              <div className="text-center mt-10">
                <Link
                  to="/services"
                  className="px-8 py-4 sm:text-xl md:text-2xl bg-cyan-500 text-white font-bold text-lg rounded-full hover:bg-cyan-600 transition duration-300 ease-in-out"
                >
                  View All Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mid CTA Strip */}
      <div className="bg-cyan-600 text-white text-center py-12 px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-4">
          Need a Same-Day Smog Check or Repair?
        </h2>
        <Link
          to="/contact"
          className="inline-block bg-white text-cyan-700 font-bold text-lg md:text-xl px-10 py-4 rounded-full hover:bg-gray-100 transition"
        >
          Plan Your Visit
        </Link>
        <p className="text-sm text-white/80 mt-3">Fast replies · Friendly team · No pressure</p>
      </div>
    </>
  );
};

export default Home;




